/**
 * Redux actions for the application.
 * @module redux/actions/app
 */

import { createAction } from "redux-actions";
import uuid from "uuid";
import {
  getConfig as apiGetConfig,
  getUserConfig as apiGetUserConfig
} from "../api/config";

import {
  contextsLoaded,
  handleReceiveCategoryList as receivedTemplateCategories,
  handleFilterOptionsReceived as receivedTemplateFilters,
  setCategoryGroups
} from "./assets";
import {
  handleReceiveCategoryList as receivedContentCategories,
  handleFilterOptionsReceived as receivedContentFilters
} from "./content";
import { channelListReceived } from "./asset";
import {
  initialize as apiInitialize,
  getExternalConnectionStatus as apiGetExternalConnectionStatus
} from "../api/camio";
import { actions as snackActions } from "../actions/snackbar";

export const CHANGE_SIDEBAR_STATE = "APP/CHANGE_SIDEBAR_STATE";
export const SET_NCS_ID = "APP/SET_NCS_ID";
export const SET_APP_MODE = "APP/SET_APP_MODE";
export const SET_RUN_CONTEXT = "APP/SET_RUN_CONTEXT";
export const LOAD_CONFIG = "APP/LOAD_CONFIG";
export const RECEIVED_CONFIG = "APP/RECEIVED_CONFIG";
export const LOAD_CONFIG_FAILED = "APP/LOAD_CONFIG_FAILED";
export const LOAD_USER_CONFIG = "APP/LOAD_USER_CONFIG";
export const RECEIVED_USER_CONFIG = "APP/RECEIVED_USER_CONFIG";
export const LOAD_USER_CONFIG_FAILED = "APP/LOAD_USER_CONFIG_FAILED";
export const CHANGE_FOOTER_HEIGHT = "APP/CHANGE_FOOTER_HEIGHT";
export const QUERY_CONNECTION_STATUS = "APP/QUERY_CONNECTION_STATUS";
export const UPDATE_CONNECTION_STATUS = "APP/UPDATE_CONNECTION_STATUS";
export const QUERY_CONNECTION_STATUS_FAILED =
  "APP/QUERY_CONNECTION_STATUS_FAILED";
export const SET_VIEWPORT_SIZE = "APP/SET_VIEWPORT_SIZE";
export const SET_APP_RUN_MODE = "APP/SET_APP_RUN_MODE";
export const SET_CONTAINER_TYPE = "APP/SET_CONTAINER_TYPE";
export const INITIALIZE = "APP/INITIALIZE";
export const INITIALIZE_COMPLETE = "APP/INITIALIZE_COMPLETE";
export const INITIALIZE_FAILED = "APP/INITIALIZE_FAILED";
export const OPEN_PLACEHOLDER = "APP/OPEN_PLACEHOLDER";

export const changeSidebarState = sidebarState =>
  createAction(CHANGE_SIDEBAR_STATE)({ sidebarState });

export const setNcsId = ncsId => createAction(SET_NCS_ID)({ ncsId });

export const setAppMode = appMode => createAction(SET_APP_MODE)({ appMode });

export const setRunContext = runContext =>
  createAction(SET_RUN_CONTEXT)({ runContext });

export const setViewPortSize = size =>
  createAction(SET_VIEWPORT_SIZE)({ size });

export const setAppRunMode = appRunMode =>
  createAction(SET_APP_RUN_MODE)({ appRunMode });

export const setContainerType = containerType =>
  createAction(SET_CONTAINER_TYPE)({ containerType });

export const openPlaceholder = placeholderId =>
  createAction(OPEN_PLACEHOLDER)({ placeholderId });

export const changeFooterHeight = footerHeight => (dispatch, getState) => {
  const state = getState().app;
  if (
    state.config.userConfig &&
    state.config.userConfig.deliveryFeedDisplay === "show"
  ) {
    return dispatch(
      createAction(CHANGE_FOOTER_HEIGHT)({ footerHeight: "5rem" })
    );
  }
  return dispatch(createAction(CHANGE_FOOTER_HEIGHT)({ footerHeight }));
};

export const initialize = userId => (dispatch, getState) => {
  dispatch(createAction(INITIALIZE)());
  const user = userId || getState().auth.user.userId;
  apiInitialize(user)
    .then(response => {
      // Populate config and user config.
      dispatch(createAction(RECEIVED_CONFIG)({ config: response.config }));
      dispatch(
        createAction(RECEIVED_USER_CONFIG)({
          userConfig: response.config.userConfig
        })
      );
      // Populate contexts
      dispatch(contextsLoaded(response.contexts));
      // Populate template-related information
      // dispatch(setCategoryGroups(response.templateAssetCategories));
      dispatch(receivedTemplateCategories(response.templateAssetCategories));
      dispatch(receivedTemplateFilters(response.templateFilters));
      dispatch(channelListReceived(response.channels));
      // Populate content-related information
      dispatch(receivedContentCategories(response.contentAssetCategories));
      dispatch(receivedContentFilters(response.contentFilters));
      return dispatch(createAction(INITIALIZE_COMPLETE)());
    })
    .catch(error => {
      dispatch(
        snackActions.showSnack(uuid.v4(), {
          content: `Failed to initialize LUCI. Error: ${error}`,
          timeout: 7000,
          buttonTemplate: "DISMISS" // change to retry button.
        })
      );
      return dispatch(createAction(INITIALIZE_FAILED)());
    });
};

export const checkConnectionStatus = () => dispatch => {
  dispatch(createAction(QUERY_CONNECTION_STATUS)());
  apiGetExternalConnectionStatus()
    .then(connections =>
      dispatch(
        createAction(UPDATE_CONNECTION_STATUS)({
          connections
        })
      )
    )
    .catch(error => {
      dispatch(createAction(QUERY_CONNECTION_STATUS_FAILED)());
      console.error(error);
    });
};

export const loadConfig = () => dispatch => {
  dispatch(createAction(LOAD_CONFIG)());
  return apiGetConfig()
    .then(response =>
      dispatch(createAction(RECEIVED_CONFIG)({ config: response }))
    )
    .catch(error => {
      console.error(error);
      return dispatch(createAction(LOAD_CONFIG_FAILED)());
    });
};

export const loadUserConfig = () => (dispatch, getState) => {
  dispatch(createAction(LOAD_USER_CONFIG));
  const userId = getState().auth.authenticated
    ? getState().auth.user.userId
    : undefined;
  return apiGetUserConfig(userId)
    .then(response =>
      dispatch(createAction(RECEIVED_USER_CONFIG)({ userConfig: response }))
    )
    .catch(error => {
      console.error(error);
      return dispatch(createAction(LOAD_USER_CONFIG_FAILED)());
    });
};

export const actions = {
  changeSidebarState,
  setNcsId,
  setAppMode,
  setRunContext,
  loadConfig,
  loadUserConfig,
  changeFooterHeight,
  setViewPortSize,
  setAppRunMode,
  setContainerType,
  initialize,
  checkConnectionStatus,
  openPlaceholder
};



// WEBPACK FOOTER //
// ./app/actions/app.js